import { Notice } from './Notice';
import { ProjectFundingAccountCoverage } from './ProjectFundingAccountCoverage';
import { ProjectFundingRequirement, projectFundingRequirementKey } from './ProjectFundingRequirement';
import { FundingNotificationStatus } from './FundingNotificationStatus';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FEATURE_FUNDING_OBJECTION_TAG } from '../constants/featureFlags';
import { useAuth } from '../context/AuthContext';
import { useResolutionOverlapWarning } from '../hooks/useResolutionOverlapWarning';
import { contractConfigApi, milestoneApi } from '../services/api';
import { PaymentMethodPicker, type PaymentMethod, type PaymentMethodEconomics } from './PaymentMethodPicker';
import { apiErrorText } from '../utils/apiError';
import { payoutAvailabilityMessage } from '../utils/payoutAvailability';
import type { VariationChange } from '../utils/variationChanges';
import type {
  MilestoneDeliveryStatus,
  ProjectFundingMilestone,
  ProjectFundingStatus
} from '../types';

const deliveryLabelKeys: Record<MilestoneDeliveryStatus, string> = {
  not_started: 'projectFunding.deliveryNotStarted',
  in_progress: 'projectFunding.deliveryInProgress',
  ready_for_attestation: 'projectFunding.deliveryReadyForAttestation',
  seller_attested: 'projectFunding.deliverySellerAttested',
  jointly_attested: 'projectFunding.deliveryJointlyAttested',
  payout_processing: 'projectFunding.deliveryPayoutProcessing',
  paid: 'projectFunding.deliveryPaid',
  disputed: 'projectFunding.deliveryDisputed',
  conflict: 'projectFunding.deliveryConflict',
  external_dispute: 'projectFunding.deliveryExternalDispute',
  under_resolution: 'projectFunding.deliveryUnderResolution',
  ready_for_reattestation: 'projectFunding.deliveryReadyForReattestation',
  payout_failed: 'projectFunding.deliveryPayoutFailed'
};

const deliveryBadge = (status: MilestoneDeliveryStatus) => {
  if (status === 'paid') return 'bg-success';
  if (status === 'disputed' || status === 'conflict' || status === 'external_dispute' || status === 'payout_failed') return 'bg-danger';
  if (status === 'payout_processing' || status === 'jointly_attested') return 'bg-info text-dark';
  if (status === 'seller_attested' || status.includes('attestation')) return 'bg-warning text-dark';
  if (status === 'in_progress' || status === 'under_resolution') return 'bg-primary';
  return 'bg-secondary';
};

const milestoneDescription = (milestone: ProjectFundingMilestone) => {
  const description = milestone.description.trim();
  const defaultDescription = new RegExp(
    `^(?:etappmål|etapp|milestone)\\s*#?\\s*${milestone.milestoneNumber}$`,
    'i'
  );

  return description && !defaultDescription.test(description) ? ` · ${description}` : '';
};

const fundingCopyKeys = (funding: ProjectFundingStatus): [string, string] => {
  if (funding.status !== 'closed' && funding.isFunded && funding.contracts.some(contract => !contract.signed)) {
    return ['projectFunding.signedContractsFundedTitle', 'projectFunding.signedContractsFundedDesc'];
  }
  switch (funding.status) {
    case 'funded':
      return ['projectFunding.fundedTitle', 'projectFunding.fundedDesc'];
    case 'partially_released':
      return ['projectFunding.partiallyReleasedTitle', 'projectFunding.partiallyReleasedDesc'];
    case 'fully_paid':
      return ['projectFunding.fullyPaidTitle', 'projectFunding.fullyPaidDesc'];
    case 'closed':
      return ['projectFunding.closedTitle', 'projectFunding.closedDesc'];
    case 'pending':
      return funding.paymentProvider === 'ping'
        ? ['pingDeposit.waitingTitle', 'pingDeposit.waitingDescription']
        : ['projectFunding.pendingTitle', 'projectFunding.pendingDesc'];
    case 'top_up_required':
      return ['projectFunding.topUpRequiredTitle', 'projectFunding.topUpRequiredDesc'];
    case 'failed':
      return ['projectFunding.failedTitle', 'projectFunding.failedDesc'];
    case 'interrupted':
      return ['projectFunding.interruptedTitle', 'projectFunding.interruptedDesc'];
    default:
      return ['projectFunding.defaultTitle', 'projectFunding.defaultDesc'];
  }
};

const formatMoney = (amountOre: number) =>
  new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: 'SEK',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amountOre / 100);


const paymentMethods: Array<{ method: PaymentMethod }> = [{ method: 'bank' }, { method: 'swish' }, { method: 'card' }];

const sellerAction = (milestone: ProjectFundingMilestone) => {
  switch (milestone.deliveryStatus) {
    case 'disputed':
      return { action: 'start_dispute_resolution' as const, labelKey: 'projectFunding.startDisputeResolution' };
    case 'under_resolution':
      return { action: 'resubmit_for_attestation' as const, labelKey: 'projectFunding.resubmitForAttestation' };
    default:
      return null;
  }
};

interface ProjectFundingPanelProps {
  projectId: string;
  /** Shown in the payment modal's title ("Betalning — Projekt #3"). */
  projectNumber?: number | string;
  refreshToken?: number;
  onFundingChanged?: (funding: ProjectFundingStatus) => void;
  onMilestoneProgressed?: (contractId: string) => Promise<void> | void;
  /** The Beställare's name, for the Hantverkare's "Väntar på att … sätter in". */
  customerName?: string | null;
  /** The most recently signed ÄTA: its surplus or top-up gets the short ÄTA card (869fnvpvp). */
  latestVariation?: VariationChange | null;
  /** Opens the Transaktioner tab, where the ÄTA is a row of its own. */
  onShowTransactions?: () => void;
}

export default function ProjectFundingPanel({
  projectId,
  projectNumber,
  refreshToken = 0,
  onFundingChanged,
  onMilestoneProgressed,
  customerName,
  latestVariation,
  onShowTransactions
}: ProjectFundingPanelProps) {
  const { t, i18n } = useTranslation();
  const { confirmOverlap, confirmationDialog } = useResolutionOverlapWarning(projectId);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [funding, setFunding] = useState<ProjectFundingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyMilestoneId, setBusyMilestoneId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [paymentMethodPickerOpen, setPaymentMethodPickerOpen] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const normalizedDepositAmount = depositAmount.replace(/\s/g, '').replace(',', '.');
  const depositAmountOre = Math.round(Number(normalizedDepositAmount) * 100);
  const accountMode = Boolean(funding?.account);
  const validDepositAmount = /^\d+(?:\.\d{0,2})?$/.test(normalizedDepositAmount) && Number.isSafeInteger(depositAmountOre) && depositAmountOre > 0 && depositAmountOre <= (funding?.remainingFundingNeedOre ?? 0);
  const [paymentContractId, setPaymentContractId] = useState<string>('');
  const [paymentContext, setPaymentContext] = useState<{ provider: 'adyen' | 'ping'; paymentId?: string } | null>(null);
  const paymentRequest = useRef(0);
  const [enabledMethods, setEnabledMethods] = useState<PaymentMethod[]>([]);
  const [paymentMethodEconomics, setPaymentMethodEconomics] = useState<
    Partial<Record<PaymentMethod, PaymentMethodEconomics>>
  >({});
  const [paymentEconomicsLoading, setPaymentEconomicsLoading] = useState(false);
  const [paymentEconomicsError, setPaymentEconomicsError] = useState<string | null>(null);

  useEffect(() => setDepositAmount(''), [projectId]);

  const load = useCallback(async () => {
    try {
      setError(null);
      const nextFunding = await contractConfigApi.getProjectFunding(projectId);
      setFunding(nextFunding);
      setDepositAmount(current => current || String((nextFunding.suggestedDepositAmountOre ?? nextFunding.nextTrustlyPaymentAmountOre) / 100));
      onFundingChanged?.(nextFunding);
    } catch {
      setError(t('projectFunding.loadError'));
    }
    finally {
      setLoading(false);
    }
  }, [onFundingChanged, projectId, t]);

  useEffect(() => {
    void load();
  }, [load, refreshToken]);

  useEffect(() => contractConfigApi.subscribeProjectPaymentEvents(
    projectId,
    () => void load()
  ), [projectId, load]);

  const closePaymentMethodPicker = useCallback(() => {
    paymentRequest.current += 1;
    setPaymentContext(null);
    setPaymentMethodEconomics({});
    setPaymentMethodPickerOpen(false);
    const params = new URLSearchParams(location.search);
    if (!params.has('paymentPicker')) return;
    params.delete('paymentPicker');
    params.delete('paymentContractId');
    const search = params.toString();
    navigate(`${location.pathname}${search ? `?${search}` : ''}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const loadPaymentMethodEconomics = useCallback(async () => {
    const requestId = ++paymentRequest.current;
    const current = () => paymentRequest.current === requestId;
    setPaymentContext(null);
    setPaymentMethodEconomics({});
    if ((!accountMode && !paymentContractId) || (accountMode && !validDepositAmount)) {
      setPaymentEconomicsLoading(false);
      return;
    }
    setPaymentEconomicsLoading(true);
    setPaymentEconomicsError(null);
    let contextLoaded = false;
    try {
      const context = await contractConfigApi.getCheckoutContext(projectId, accountMode ? undefined : paymentContractId);
      if (!current()) return;
      if (context.provider !== 'ping' && context.provider !== 'adyen') throw new Error('Unknown payment provider.');
      contextLoaded = true;
      if (context.provider === 'ping') {
        if (accountMode) {
          setPaymentContext({ provider: 'ping', paymentId: context.paymentId });
          return;
        }
        const params = new URLSearchParams({ projectId, contractId: paymentContractId, method: 'bank', provider: 'ping' });
        if (context.paymentId) params.set('paymentId', context.paymentId);
        setPaymentMethodPickerOpen(false);
        navigate(`/payment-checkout?${params}`);
        return;
      }
      const runtime = await contractConfigApi.getAdyenRuntime();
      if (!current()) return;
      // The methods Admin has switched on (ClickUp 869fnhhhb); an older API
      // only says whether cards are allowed.
      const switchedOn: PaymentMethod[] = runtime.paymentMethods
        ?? paymentMethods.map(({ method }) => method).filter(method => method !== 'card' || runtime.cardPaymentsEnabled === true);
      const visibleMethods = paymentMethods.filter(({ method }) => switchedOn.includes(method));
      const entries = await Promise.all(
        visibleMethods.map(async ({ method }) => {
          const economics = await contractConfigApi.getProjectEconomics(
            projectId,
            method,
            accountMode ? undefined : paymentContractId,
            undefined,
            accountMode ? depositAmountOre : undefined
          );
          const optional = (value: unknown) => (Number.isFinite(Number(value)) ? Number(value) : 0);
          const parsed: PaymentMethodEconomics = {
            projectValue: Number(economics?.projectValue),
            customerFee: Number(economics?.customerFee),
            paymentMethodFee: Number(economics?.paymentMethodFee ?? 0),
            projectFee: optional(economics?.projectFee ?? economics?.customerProjectFee),
            minimumFeeAdjustment: optional(economics?.minimumFeeAdjustment ?? economics?.customerMinimumFeeAdjustment)
          };
          if (![parsed.projectValue, parsed.customerFee, parsed.paymentMethodFee].every(Number.isFinite)) {
            throw new Error('Invalid payment economics.');
          }
          return [method, parsed] as const;
        })
      );
      if (!current()) return;
      setPaymentContext({ provider: 'adyen', paymentId: context.paymentId });
      setEnabledMethods(visibleMethods.map(({ method }) => method));
      setPaymentMethodEconomics(Object.fromEntries(entries));
    } catch {
      if (!current()) return;
      setPaymentContext(null);
      setPaymentMethodEconomics({});
      setPaymentEconomicsError(t(contextLoaded ? 'projectFunding.economicsError' : 'pingDeposit.contextError'));
    } finally {
      if (current()) setPaymentEconomicsLoading(false);
    }
  }, [paymentContractId, projectId, navigate, t, accountMode, depositAmountOre, validDepositAmount]);

  useEffect(() => {
    if (paymentMethodPickerOpen) void loadPaymentMethodEconomics();
    return () => { paymentRequest.current += 1; };
  }, [loadPaymentMethodEconomics, paymentMethodPickerOpen]);

  const isSeller = user?.personaType === 'company';
  const isBuyer = user?.personaType === 'person';

  useEffect(() => {
    if (
      funding &&
      isBuyer &&
      new URLSearchParams(location.search).get('paymentPicker') === 'open'
    ) {
      const eligible = funding.contracts.filter(
        (contract) => contract.canPay || contract.canResumePayment
      );
      const requestedContractId = new URLSearchParams(location.search).get('paymentContractId');
      // A contract-node action must never silently select a different contract.
      if (requestedContractId && !eligible.some(contract => contract.contractId === requestedContractId)) {
        setPaymentMethodPickerOpen(false);
        return;
      }
      if (eligible.length > 0) {
        setPaymentContractId((current) =>
          requestedContractId ?? (eligible.some((contract) => contract.contractId === current)
            ? current
            : eligible.length === 1 ? eligible[0].contractId : '')
        );
        setPaymentMethodPickerOpen(true);
      }
    }
  }, [funding, isBuyer, location.search]);

  const progress = async (milestone: ProjectFundingMilestone) => {
    const next = sellerAction(milestone);
    if (!next) return;
    setBusyMilestoneId(milestone.milestoneId);
    try {
      if (next.action === 'start_dispute_resolution' &&
        !await confirmOverlap('remediation', milestone.contractId)) return;
      await milestoneApi.progressMilestone(milestone.milestoneId, next.action);
      await onMilestoneProgressed?.(milestone.contractId);
      await load();
    } catch (failure) {
      setError(apiErrorText(t, failure, 'projectFunding.progressError'));
    } finally {
      setBusyMilestoneId(null);
    }
  };

  if (loading) {
    return null;
  }

  if (!funding) {
    return error ? <div className="alert alert-warning mb-4">{error}</div> : null;
  }

  // Funding becomes relevant per signed contract, even if another is still a draft.
  if (funding.status === 'unfunded' && !funding.contracts.some(contract => contract.signed)) {
    return null;
  }

  if (funding.status === 'closed') {
    const [titleKey, descriptionKey] = fundingCopyKeys(funding);
    return (
      <div className="alert alert-success mb-4" data-testid="project-funding-closed">
        <div className="fw-semibold">
          <i className="feather-check-circle me-2" />
          {t(titleKey)}
        </div>
        <div className="text-explain mt-1">{t(descriptionKey)}</div>
      </div>
    );
  }

  const [titleKey, descriptionKey] = fundingCopyKeys(funding);
  const secured = funding.isFunded;
  const unsigned = funding.contracts.filter(contract => !contract.signed);
  const description = t(descriptionKey, {
    count: unsigned.length,
    numbers: unsigned.map(contract => `#${contract.contractNumber}`).join(', ')
  });
  const eligiblePaymentContracts = funding.contracts.filter(
    (contract) => contract.canPay || contract.canResumePayment
  );
  const canResumePayment = eligiblePaymentContracts.some(
    (contract) => contract.canResumePayment
  );
  const canRetryPayment = eligiblePaymentContracts.length > 0;
  const openPaymentMethodPicker = () => {
    setDepositAmount(String((funding.suggestedDepositAmountOre ?? funding.nextTrustlyPaymentAmountOre) / 100));
    setPaymentContractId(accountMode ? eligiblePaymentContracts[0]?.contractId ?? '' : eligiblePaymentContracts.length === 1 ? eligiblePaymentContracts[0].contractId : '');
    setPaymentMethodPickerOpen(true);
  };
  const pingNotices = funding.contracts.filter(contract => contract.paymentProvider === 'ping' && contract.paymentId);
  // A paid notice remains a receipt when a later ÄTA needs more funding.
  // Only a pending payment can be resumed instead of creating the top-up.
  const unpaidPingNotices = pingNotices.filter(contract => contract.remainingFundingNeedOre > 0 && contract.canResumePayment);
  const paidPingNotices = pingNotices.filter(contract => !contract.canResumePayment);
  const pingNoticeUrl = (contractId: string, paymentId: string) =>
    `/payment-checkout?${new URLSearchParams({ projectId, contractId, paymentId, provider: 'ping', method: 'bank' })}`;
  const noticeActions = isBuyer && unpaidPingNotices.map(contract => (
    <Link key={contract.contractId} className="btn btn-primary project-funding-action"
      to={pingNoticeUrl(contract.contractId, contract.paymentId!)}>
      <i className="bi bi-receipt me-2" aria-hidden="true" />
      {t(funding.contracts.length === 1 ? 'pingDeposit.openNotice' : 'pingDeposit.openContractNotice', { number: contract.contractNumber })}
    </Link>
  ));
  const showPaymentPicker = isBuyer && canRetryPayment && eligiblePaymentContracts.some(
    contract => !unpaidPingNotices.some(notice => notice.contractId === contract.contractId)
  );
  const attentionMilestone = funding.milestones.find(milestone =>
    !milestone.payoutAvailability &&
    // An objection is shown on its stage, not here (ClickUp 869fh4ad1).
    (FEATURE_FUNDING_OBJECTION_TAG ? ['disputed', 'under_resolution', 'payout_failed'] : ['payout_failed'])
      .includes(milestone.deliveryStatus)
  );
  const hasSurplus = funding.surplusAmountOre > 0;

  // After a signed ÄTA (ClickUp 869fnvpvp) the card says only what it means
  // for the money: the surplus that is paid back, or how much the funding
  // grows. Key figures, deposits per contract and the stage plan stay out of
  // it; the Transaktioner tab shows the ÄTA as a row of its own.
  const variationSurplus = hasSurplus && latestVariation != null && latestVariation.deltaOre < 0;
  const variationTopUp = !secured && latestVariation != null && latestVariation.deltaOre > 0 &&
    funding.contracts.some(contract => contract.contractId === latestVariation.contractId && contract.remainingFundingNeedOre > 0);
  const variationKicker = (kind: 'surplus' | 'topUp') => latestVariation?.number != null
    ? t(`projectFunding.variation.${kind}Kicker`, { number: latestVariation.number })
    : t(`projectFunding.variation.${kind}KickerNoNumber`);
  const transactionsLink = onShowTransactions && (
    <button type="button" className="btn btn-link status-card-toggle" onClick={onShowTransactions} data-testid="project-funding-variation-transactions">
      {t('projectFunding.variation.showTransactions')}
      <i className="feather-arrow-right ms-1" aria-hidden="true" />
    </button>
  );

  if (!accountMode && variationSurplus) {
    const amount = formatMoney(funding.surplusAmountOre);
    return (
      <section className="status-card is-waiting" data-testid="project-funding-variation" data-variation="surplus" aria-labelledby="project-funding-variation-title">
        <div className="status-card-head">
          <div className="status-card-main">
            <div className="status-card-kicker">{variationKicker('surplus')}</div>
            <h2 id="project-funding-variation-title" className="status-card-title">
              {t(isBuyer ? 'projectFunding.variation.surplusTitleBuyer' : 'projectFunding.variation.surplusTitle', { amount })}
            </h2>
          </div>
          {transactionsLink}
        </div>
      </section>
    );
  }

  if (!accountMode && secured && !attentionMilestone && !hasSurplus) {
    return pingNotices.length || unsigned.length ? <section className="project-funding-panel alert alert-success d-flex flex-wrap align-items-center justify-content-between gap-3" data-testid="project-funding-confirmed">
      <div><strong>{t(titleKey)}</strong><p className="mb-0">{description}</p></div>
      {isBuyer && <div className="d-flex flex-wrap gap-3">{pingNotices.map(contract => (
        <Link key={contract.contractId} className="project-funding-notice-link d-inline-flex align-items-center gap-2 text-decoration-underline"
          to={pingNoticeUrl(contract.contractId, contract.paymentId!)}>
          <i className="bi bi-file-earmark-text" aria-hidden="true" />
          {t('pingDeposit.viewPaidContractNotice', { number: contract.contractNumber })}
        </Link>
      ))}</div>}
    </section> : null;
  }

  // Folded deviation (ClickUp 869fnv5f2): a status card with what has
  // happened and "Visa detaljer", which opens the full panel below.
  if (!accountMode && secured && !expanded) {
    // A price adjustment row has no stage number of its own to name.
    const number = attentionMilestone && attentionMilestone.itemType !== 'variation_price_adjustment'
      ? attentionMilestone.milestoneNumber
      : null;
    // With more than one contract every contract numbers its stages from 1, so
    // the title names the contract too: "kontrakt #1, etapp 2".
    const stage = number == null ? null : funding.contracts.length > 1 && attentionMilestone?.contractNumber != null
      ? t('projectFunding.card.stageWithContract', { contract: attentionMilestone.contractNumber, number })
      : t('projectFunding.card.stage', { number });
    const withNumber = (key: string) => stage != null
      ? t(`projectFunding.card.${key}`, { stage })
      : t(`projectFunding.card.${key}NoNumber`);
    const [kickerKey, title, variant]: [string, string, string] = hasSurplus
      ? ['surplusKicker', t('projectFunding.card.surplusTitle', { amount: formatMoney(funding.surplusAmountOre) }), 'is-waiting']
      : attentionMilestone?.deliveryStatus === 'disputed'
      ? ['disputedKicker', withNumber(isBuyer ? 'disputedTitleOwn' : 'disputedTitle'), 'is-alert']
      : attentionMilestone?.deliveryStatus === 'under_resolution'
      ? ['underResolutionKicker', withNumber('underResolutionTitle'), 'is-waiting']
      : ['payoutFailedKicker', withNumber('payoutFailedTitle'), 'is-alert'];
    return (
      <section className={`status-card ${variant}`} data-testid="project-funding-status-tag" aria-labelledby="project-funding-status-title">
        <div className="status-card-head">
          <div className="status-card-main">
            <div className="status-card-kicker">{t(`projectFunding.card.${kickerKey}`)}</div>
            <h2 id="project-funding-status-title" className="status-card-title">{title}</h2>
          </div>
          <button
            type="button"
            className="btn btn-link status-card-toggle"
            aria-expanded="false"
            onClick={() => setExpanded(true)}
          >
            {t('projectFunding.card.showDetails')}
            <i className="feather-chevron-down ms-1" aria-hidden="true" />
          </button>
        </div>
      </section>
    );
  }

  const paymentPicker = paymentMethodPickerOpen && (
    <PaymentMethodPicker
      projectId={projectId}
      projectNumber={projectNumber}
      provider={paymentContext?.provider ?? null}
      projectAccount={accountMode}
      depositAmount={depositAmount}
      onDepositAmountChange={setDepositAmount}
      maximumAmountOre={funding.remainingFundingNeedOre}
      requirementAmountOre={funding.account?.missingAmountOre ?? 0}
      fundingAccount={funding.account}
      validAmount={!accountMode || validDepositAmount}
      contracts={eligiblePaymentContracts}
      contractId={paymentContractId}
      onContractChange={contractId => {
        paymentRequest.current += 1;
        setPaymentContext(null);
        setPaymentContractId(contractId);
        setPaymentMethodEconomics({});
        setPaymentEconomicsError(null);
      }}
      enabledMethods={enabledMethods}
      economics={paymentMethodEconomics}
      loading={paymentEconomicsLoading}
      error={paymentEconomicsError}
      onRetry={() => void loadPaymentMethodEconomics()}
      onClose={closePaymentMethodPicker}
      onProceed={method => {
        if (!paymentContext || paymentEconomicsLoading || paymentEconomicsError) return;
        const params = new URLSearchParams(accountMode ? { projectId, method } : { projectId, contractId: paymentContractId, method });
        if (accountMode) params.set('amountOre', String(depositAmountOre));
        else params.set('contractId', paymentContractId);
        if (paymentContext.provider === 'ping') {
          params.set('provider', 'ping');
          if (paymentContext.paymentId) params.set('paymentId', paymentContext.paymentId);
        }
        navigate(`/payment-checkout?${params}`);
      }}
    />
  );

  if (funding.account) {
    const account = funding.account;
    return <>
      {confirmationDialog}
      <section className="project-funding-awaiting mb-4" data-testid="project-funding-panel">
        <button
          type="button"
          className={`project-funding-account-toggle${account.fulfilled ? ' is-fulfilled' : ''}`}
          aria-expanded={expanded}
          aria-controls="project-funding-account-body"
          onClick={() => setExpanded(open => !open)}
          data-testid="project-funding-toggle"
        >
          <i className={`bi ${account.fulfilled ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-2`} aria-hidden="true" />
          {t(account.fulfilled ? 'projectFunding.account.title' : 'projectFunding.pendingTitle')}
          <i className={`bi bi-chevron-${expanded ? 'up' : 'down'} ms-2`} aria-hidden="true" />
        </button>
        {!isBuyer && account.fulfilled && account.remainingMilestoneCount > 0 && <Notice level="done" title={t('fundingNotice.projectAccount.planningTitle')} testId="funding-ready-to-plan">
          <p className="text-explain mb-0">{t('fundingNotice.projectAccount.planning')}</p>
        </Notice>}
        {!expanded && <ProjectFundingRequirement account={account} />}
        {expanded && <div id="project-funding-account-body" className="card project-funding-panel">
        <div className="card-body">
          <h2 className="h5">{t('projectFunding.account.title')}</h2>
          {error && <div className="alert alert-warning">{error}</div>}
          <div className="row g-3">
            <div className="col-12 col-sm-6 col-lg-3"><div className="text-explain">{t('projectFunding.account.balance')}</div><strong className="fs-3">{formatMoney(account.balanceOre)}</strong></div>
            <div className="col-12 col-sm-6 col-lg-3"><div className="text-explain">{t('projectFunding.account.deposited')}</div><strong>{formatMoney(funding.fundedAmountOre)}</strong></div>
            <div className="col-12 col-sm-6 col-lg-3"><div className="text-explain">{t('projectFunding.account.spent')}</div><strong>{formatMoney(funding.paidOutAmountOre)}</strong><div className="text-explain">{t('projectFunding.account.spentHint')}</div></div>
            <div className="col-12 col-sm-6 col-lg-3"><div className="text-explain">{t('projectFunding.account.remaining')}</div><strong>{formatMoney(funding.remainingFundingNeedOre)}</strong></div>
          </div>
          {account.awaitingPayoutAmountOre > 0 && <p className="text-explain mt-3">{t('projectFunding.account.committed', { amount: formatMoney(account.awaitingPayoutAmountOre) })}</p>}
          <div className={`alert ${account.fulfilled ? 'alert-success' : 'alert-warning'} mt-3`} role="status">
            <strong>{account.remainingMilestoneCount === 0 ? t('projectFunding.account.finished') : account.fulfilled ? t('projectFunding.account.fulfilled') : t('projectFunding.account.missing', { amount: formatMoney(account.missingAmountOre) })}</strong>
            {account.remainingMilestoneCount > 0 && <p className="text-explain mb-2">{t(projectFundingRequirementKey(account), { count: account.startedMilestoneCount || account.milestoneIds.length, amount: formatMoney(account.requiredAmountOre) })}</p>}
            <ProjectFundingAccountCoverage funding={funding} />
            {!account.fulfilled && <p className="text-explain mt-2 mb-0">{t(isBuyer ? 'projectFunding.account.reminders' : 'projectFunding.account.remindersSeller')}</p>}
          </div>
          {isBuyer && funding.remainingFundingNeedOre > 0 && <button type="button" className="btn btn-primary project-funding-action" onClick={openPaymentMethodPicker}>{t('projectFunding.payIn')}</button>}
          {isBuyer && <p className="text-explain mt-2">{t('projectFunding.account.freeAmount')}</p>}
          {funding.milestones.length > 0 && <div className="mt-4">
            <h3 className="h6">{t('projectFunding.lockedMilestonePlanHeading')}</h3>
            <ul className="list-group list-group-flush">
              {funding.milestones.filter(milestone => !milestone.itemType || milestone.itemType === 'milestone').map(milestone => {
                const committed = ['paid', 'jointly_attested', 'payout_processing', 'payout_failed'].includes(milestone.deliveryStatus)
                  || (milestone.companyAttestation === 'approved' && milestone.personAttestation === 'approved');
                const inRequirement = account.milestoneIds.includes(milestone.milestoneId);
                return <li key={milestone.milestoneId} className="list-group-item d-flex flex-wrap justify-content-between gap-2 px-0" data-testid={`project-stage-coverage-${milestone.milestoneId}`}>
                  <span>{t(funding.contracts.length > 1 ? 'projectFunding.card.stageWithContract' : 'projectFunding.milestoneHeading', { contract: milestone.contractNumber, number: milestone.milestoneNumber })}{milestoneDescription(milestone)}</span>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <span>{formatMoney(milestone.afterRotAmountOre)}</span>
                    {committed ? <span className="badge bg-secondary">{t(deliveryLabelKeys[milestone.deliveryStatus])}</span> : <>
                      {isBuyer && <span className="badge bg-light text-dark">{t(inRequirement ? 'projectFunding.account.inRequirement' : 'projectFunding.account.later')}</span>}
                      {milestone.fundingCoverage && <span className={`badge ${milestone.fundingCoverage === 'covered' ? 'bg-success' : milestone.fundingCoverage === 'partial' ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                        {t(`projectFunding.account.coverage.${milestone.fundingCoverage}`)}
                      </span>}
                      {(milestone.uncoveredAmountOre ?? 0) > 0 && <span className="text-explain">{t('projectFunding.account.stageMissing', { amount: formatMoney(milestone.uncoveredAmountOre!) })}</span>}
                    </>}
                  </div>
                </li>;
              })}
            </ul>
            <p className="text-explain mt-3 mb-0">{t('projectFunding.account.orderExplanation')}</p>
          </div>}
          {isSeller && <FundingNotificationStatus projectId={projectId} refreshToken={refreshToken} compact />}
        </div>
        </div>}
      </section>
      {paymentPicker}
    </>;
  }

  // Not yet funded (ClickUp 869fe7hrz, card 869fnv5f2): a status card says
  // who is to deposit how much and why, with "Sätt in" for the Beställare
  // (the Hantverkare sees that it waits on the Beställare). "Visa mer" opens
  // the key figures and the locked stage plan. Saknas = Projektsumma −
  // Finansierat: both count the signed contracts only.
  if (variationTopUp) {
    const amount = formatMoney(funding.remainingFundingNeedOre);
    const showBuyerActions = isBuyer && (showPaymentPicker || unpaidPingNotices.length > 0);
    return (
      <>
      {confirmationDialog}
      <section className="status-card is-waiting" data-testid="project-funding-variation" data-variation="top-up" aria-labelledby="project-funding-variation-title">
        <div className="status-card-head">
          <div className="status-card-main">
            <div className="status-card-kicker">{variationKicker('topUp')}</div>
            <h2 id="project-funding-variation-title" className="status-card-title">{t('projectFunding.variation.topUpTitle', { amount })}</h2>
            <p className="text-explain mb-0">
              {isBuyer
                ? t('projectFunding.variation.topUpBuyer')
                : customerName
                ? t('projectFunding.variation.topUpSeller', { name: customerName })
                : t('projectFunding.variation.topUpSellerNoName')}
            </p>
          </div>
          {showBuyerActions && (
            <div className="project-funding-actions status-card-actions">
              {noticeActions}
              {showPaymentPicker && (
                <button type="button" className="btn btn-primary project-funding-action" onClick={openPaymentMethodPicker}>
                  <i className="bi bi-credit-card me-2" aria-hidden="true" />
                  {canResumePayment ? t('projectFunding.resumePayment') : t('projectFunding.payIn')}
                </button>
              )}
            </div>
          )}
        </div>
        {transactionsLink}
      </section>
      {paymentPicker}
      </>
    );
  }

  if (!secured) {
    const unsignedOre = unsigned.reduce((sum, contract) => sum + (contract.afterRotAmountOre || contract.grossAmountOre), 0);
    const missing = funding.remainingFundingNeedOre > 0;
    const amount = formatMoney(funding.remainingFundingNeedOre);
    const awaitingTitle = !missing
      ? t('projectFunding.card.processingTitle')
      : isBuyer
      ? t('projectFunding.card.buyerTitle', { amount })
      : customerName
      ? t('projectFunding.card.sellerTitle', { name: customerName, amount })
      : t('projectFunding.card.sellerTitleNoName', { amount });
    // Also when nothing is left to fund yet (e.g. a payment on its way): the
    // buyer can still resume or open the payment from the card.
    const showBuyerActions = isBuyer && (showPaymentPicker || unpaidPingNotices.length > 0);
    return (
      <>
      {confirmationDialog}
      <section className="status-card is-waiting project-funding-awaiting" data-testid="project-funding-panel" aria-labelledby="project-funding-awaiting-title">
        <div className="status-card-head" data-testid={missing ? 'project-funding-shortage' : undefined}>
          <div className="status-card-main">
            <div className="status-card-kicker">{t('projectFunding.card.depositKicker')}</div>
            <h2 id="project-funding-awaiting-title" className="status-card-title">{awaitingTitle}</h2>
            {missing && (
              <p className="text-explain mb-0">{t('projectFunding.reason', { reason: funding.fundingReason })}</p>
            )}
          </div>
          {showBuyerActions && (
            <div className="project-funding-actions status-card-actions">
              {noticeActions}
              {showPaymentPicker && (
                <button type="button" className="btn btn-primary project-funding-action" onClick={openPaymentMethodPicker}>
                  <i className="bi bi-credit-card me-2" aria-hidden="true" />
                  {canResumePayment ? t('projectFunding.resumePayment') : t('projectFunding.payIn')}
                </button>
              )}
            </div>
          )}
          {!isBuyer && missing && (
            <span className="status-card-pill" data-testid="project-funding-waiting-on-customer">
              <i className="feather-clock" aria-hidden="true" />
              {t('projectFunding.awaitingCustomerPayment')}
            </span>
          )}
        </div>
        {/* Outside the shortage row: that row holds only the deposit actions. */}
        <button
          type="button"
          className="btn btn-link status-card-toggle"
          aria-expanded={expanded}
          aria-controls="project-funding-awaiting-body"
          onClick={() => setExpanded(open => !open)}
          data-testid="project-funding-toggle"
        >
          {t(expanded ? 'projectFunding.card.showLess' : 'projectFunding.card.showMore')}
          <i className={`feather-chevron-${expanded ? 'up' : 'down'} ms-1`} aria-hidden="true" />
        </button>
        {expanded && (
          <div id="project-funding-awaiting-body" className="project-funding-awaiting-body">
            {error && <div className="alert alert-warning py-2 mb-0">{error}</div>}
            {isBuyer && !canRetryPayment && unsigned.length > 0 && (
              <div className="alert alert-info py-2 mb-0" role="status">{t('projectFunding.signingRequiredInfo')}</div>
            )}
            {missing && funding.paymentProvider !== 'ping' && funding.requiresMultipleTrustlyPayments && (
              <p className="text-explain mb-0">{t('projectFunding.trustlyLimit', { amount: formatMoney(funding.trustlyPaymentLimitOre) })}</p>
            )}
            {hasSurplus && (
              <div className="alert alert-info mb-0" data-testid="project-funding-surplus">
                {t('projectFunding.surplusPrefix')}{' '}
                <strong>{formatMoney(funding.surplusAmountOre)}</strong>{t('projectFunding.surplusSuffix')}
              </div>
            )}
            <dl className="project-funding-awaiting-figures" data-testid="project-funding-figures">
              <div><dt>{t('projectFunding.projectSum')}</dt><dd>{formatMoney(funding.fundedAmountOre + funding.remainingFundingNeedOre)}</dd></div>
              <div><dt>{t('projectFunding.summaryFunded')}</dt><dd>{formatMoney(funding.fundedAmountOre)}</dd></div>
              <div><dt>{t('projectFunding.summaryMissing')}</dt><dd>{formatMoney(funding.remainingFundingNeedOre)}</dd></div>
              <div><dt>{t('projectFunding.summaryReserved')}</dt><dd>{formatMoney(funding.reservedAmountOre)}</dd></div>
            </dl>
            {isBuyer && paidPingNotices.length > 0 && (
              // Receipts for contracts already paid with Ping stay reachable
              // while the rest of the project still waits for funding.
              <div className="d-flex flex-wrap gap-3" data-testid="project-funding-paid-notices">
                {paidPingNotices.map(contract => (
                  <Link key={contract.contractId} data-contract-id={contract.contractId}
                    className="project-funding-notice-link d-inline-flex align-items-center gap-2 text-decoration-underline"
                    to={pingNoticeUrl(contract.contractId, contract.paymentId!)}>
                    <i className="bi bi-file-earmark-text" aria-hidden="true" />
                    {t('pingDeposit.viewPaidContractNotice', { number: contract.contractNumber })}
                  </Link>
                ))}
              </div>
            )}
            {unsigned.length > 0 && (
              <p className="project-funding-awaiting-note" data-testid="project-funding-unsigned-note">
                {t('projectFunding.unsignedNote', {
                  count: unsigned.length,
                  numbers: unsigned.map(contract => `#${contract.contractNumber}`).join(', '),
                  amount: formatMoney(unsignedOre)
                })}
              </p>
            )}
            {funding.milestones.length > 0 && (
              <div className="project-funding-awaiting-plan">
                <h3>{t('projectFunding.lockedMilestonePlanHeading')}</h3>
                <ul>
                  {funding.milestones.map(milestone => {
                    const withRot = (funding.contracts.find(contract => contract.contractId === milestone.contractId)?.rotAmountOre ?? 0) !== 0;
                    return (
                      <li key={milestone.milestoneId}>
                        <strong>
                          {milestone.contractNumber != null &&
                            t('projectFunding.milestoneContractPrefix', { number: milestone.contractNumber })}
                          {milestone.itemType === 'variation_price_adjustment'
                            ? milestone.amountOre === 0
                              ? t('projectFunding.variationChange')
                              : t('projectFunding.variationPriceChange')
                            : t('projectFunding.milestoneHeading', { number: milestone.milestoneNumber })}
                          {milestoneDescription(milestone)}
                        </strong>
                        <span>
                          {t(milestone.payoutAvailability ? 'payoutAvailability.badge' : deliveryLabelKeys[milestone.deliveryStatus])} — {formatMoney(withRot ? milestone.afterRotAmountOre : milestone.grossAmountOre)}
                        </span>
                        {milestone.disputeReason && (
                          <span className="text-danger">
                            <strong>{t('projectFunding.objectionLabel')}</strong> {milestone.disputeReason}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {isSeller && <FundingNotificationStatus projectId={projectId} refreshToken={refreshToken} compact />}
          </div>
        )}
      </section>
      {paymentPicker}
      </>
    );
  }

  return (
    <>
    {confirmationDialog}
    <section className="card project-funding-panel mb-4" data-testid="project-funding-panel">
      <div className="card-body">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <i className="feather-shield text-success fs-4" aria-hidden="true" />
              <h5 className="mb-0">{t(titleKey)}</h5>
            </div>
            <p className="text-muted mb-0">{description}</p>
          </div>
          <button
            type="button"
            className="btn btn-link status-card-toggle"
            aria-expanded="true"
            aria-label={t('projectFunding.collapseAriaLabel')}
            onClick={() => setExpanded(false)}
          >
            {t('projectFunding.collapse')}
            <i className="feather-chevron-up ms-1" aria-hidden="true" />
          </button>
        </div>

        {error && <div className="alert alert-warning py-2 mt-3 mb-0">{error}</div>}

        {hasSurplus && (
          <div className="alert alert-info mt-3 mb-0" data-testid="project-funding-surplus">
            {t('projectFunding.surplusPrefix')}{' '}
            <strong>{formatMoney(funding.surplusAmountOre)}</strong>{t('projectFunding.surplusSuffix')}
          </div>
        )}

        <div className="row row-cols-2 row-cols-lg-5 g-3 mt-1">
          <div className="col">
            <small className="text-muted d-block">{t('projectFunding.summaryProjectAmount')}</small>
            <strong>{formatMoney(funding.totalAmountOre)}</strong>
          </div>
          <div className="col">
            <small className="text-muted d-block">{t('projectFunding.summaryFunded')}</small>
            <strong className="text-success">{formatMoney(funding.fundedAmountOre)}</strong>
          </div>
          <div className="col">
            <small className="text-muted d-block">{t('projectFunding.summaryReserved')}</small>
            <strong>{formatMoney(funding.reservedAmountOre)}</strong>
          </div>
          <div className="col">
            <small className="text-muted d-block">{t('projectFunding.summaryPaidOut')}</small>
            <strong>{formatMoney(funding.paidOutAmountOre)}</strong>
          </div>
        </div>

        {isSeller && <FundingNotificationStatus projectId={projectId} refreshToken={refreshToken} />}

        {funding.contracts.length > 0 && (
          <div className="mt-4" data-testid="contract-funding-list">
            <h6 className="fw-semibold">{t('projectFunding.contractPaymentsHeading')}</h6>
            <div className="d-flex flex-column gap-2">
              {funding.contracts.map((contract) => (
                <div className="border rounded p-3" key={contract.contractId} data-contract-id={contract.contractId}>
                  <div className="d-flex flex-wrap justify-content-between gap-3">
                    <div>
                      <div className="fw-semibold">
                        {t(contract.contractName ? 'projectFunding.contractLine' : 'pingDeposit.document.contractNumber', { number: contract.contractNumber, description: contract.contractName })}
                      </div>
                      <div className={`small mt-1 ${contract.status === 'funded' ? 'text-success' : 'text-muted'}`}>
                        <i className={`bi ${contract.status === 'funded' ? 'bi-check-circle' : 'bi-info-circle'} me-1`} aria-hidden="true" />
                        {contract.status === 'funded'
                          ? t('projectFunding.summaryFunded')
                          : contract.signed ? t('projectFunding.contractSigned') : t('projectFunding.contractAwaitingSigning')}
                      </div>
                      {isBuyer && contract.paymentProvider === 'ping' && contract.paymentId && contract.remainingFundingNeedOre <= 0 && (
                        <Link className="project-funding-notice-link d-inline-flex align-items-center gap-2 text-decoration-underline"
                          to={pingNoticeUrl(contract.contractId, contract.paymentId)}>
                          <i className="bi bi-file-earmark-text" aria-hidden="true" />
                          {t('pingDeposit.viewPaidNotice')}
                        </Link>
                      )}
                    </div>
                    <div className="text-start text-sm-end">
                      {contract.rotAmountOre !== 0 ? (
                        <>
                          <div>{t('projectFunding.beforeRotLabel')} <strong>{formatMoney(contract.grossAmountOre)}</strong></div>
                          <div className="small text-muted">
                            {t('projectFunding.plannedRot', { amount: formatMoney(contract.rotAmountOre) })}
                          </div>
                          <div>{t('projectFunding.afterRotLabel')} <strong>{formatMoney(contract.afterRotAmountOre)}</strong></div>
                        </>
                      ) : (
                        <div>{t('projectFunding.contractAmountLabel')} <strong>{formatMoney(contract.grossAmountOre)}</strong></div>
                      )}
                      <div className="small text-muted">
                        {t('projectFunding.contractFunded', { amount: formatMoney(contract.fundedAmountOre) })}
                      </div>
                      {/* What is left to deposit, not the contract's whole amount: an ÄTA is a
                          cumulative version that includes what is already funded (ClickUp 869fgj3uh). */}
                      {contract.remainingFundingNeedOre > 0 && (
                        <div data-testid={`project-funding-contract-to-pay-${contract.contractId}`}>
                          {t('projectFunding.toPayLabel')} <strong>{formatMoney(contract.remainingFundingNeedOre)}</strong>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {funding.milestones.length > 0 && (
          <div className="mt-4">
            <h6 className="fw-semibold">{t('projectFunding.lockedMilestonePlanHeading')}</h6>
            <div className="d-flex flex-column gap-2">
              {funding.milestones.map((milestone) => {
                const action = isSeller ? sellerAction(milestone) : null;
                return (
                  <div key={milestone.milestoneId} className="border rounded p-3">
                    <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                      <div>
                        <div className="fw-semibold">
                          {milestone.contractNumber != null &&
                            t('projectFunding.milestoneContractPrefix', { number: milestone.contractNumber })}
                          {milestone.itemType === 'variation_price_adjustment'
                            ? milestone.amountOre === 0
                              ? t('projectFunding.variationChange')
                              : t('projectFunding.variationPriceChange')
                            : t('projectFunding.milestoneHeading', { number: milestone.milestoneNumber })}
                          {milestoneDescription(milestone)}
                        </div>
                        <span className={`badge ${milestone.payoutAvailability ? 'bg-info text-dark' : deliveryBadge(milestone.deliveryStatus)} mt-1`}>
                          {t(milestone.payoutAvailability ? 'payoutAvailability.badge' : deliveryLabelKeys[milestone.deliveryStatus])}
                        </span>
                        {isSeller && milestone.payoutAvailability && <p className="text-explain mt-2 mb-0">
                          {payoutAvailabilityMessage(t, milestone.payoutAvailability, i18n.language)}
                        </p>}
                        {milestone.disputeReason && (
                          <div className="small text-danger mt-2">
                            <strong>{t('projectFunding.objectionLabel')}</strong> {milestone.disputeReason}
                          </div>
                        )}
                      </div>
                      <div className="text-end">
                        {(funding.contracts.find(
                          (contract) => contract.contractId === milestone.contractId
                        )?.rotAmountOre ?? 0) !== 0 ? (
                          <>
                            <div className="small text-muted">{t('projectFunding.milestoneBeforeRot', { amount: formatMoney(milestone.grossAmountOre) })}</div>
                            <div className="small text-muted">{t('projectFunding.milestoneRot', { amount: formatMoney(milestone.rotAmountOre) })}</div>
                            <div className="fw-bold">{t('projectFunding.milestoneAfterRot', { amount: formatMoney(milestone.afterRotAmountOre) })}</div>
                          </>
                        ) : (
                          <div className="fw-bold">{formatMoney(milestone.grossAmountOre)}</div>
                        )}
                        {milestone.payoutAmountOre != null && (
                          <small className="text-muted d-block">
                            {t('projectFunding.netToSeller', { amount: formatMoney(milestone.payoutAmountOre) })}
                          </small>
                        )}
                        {action && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary mt-2"
                            disabled={busyMilestoneId === milestone.milestoneId}
                            onClick={() => void progress(milestone)}
                          >
                            {busyMilestoneId === milestone.milestoneId && (
                              <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
                            )}
                            {t(action.labelKey)}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>

    {paymentPicker}
    </>
  );
}
